/**
 * The board toolbar (spec M1-B4 §8.11), measured on `[ui:library/table-01-top.png]`.
 *
 * Order, left to right: `New item` (+ 28 x 32 caret) · `Search` · `Person` · `Filter` (+ caret)
 * · `Sort` · `Hide` · `Group by` · `⋯` · a collapse chevron at the right edge. The band is
 * 32 px tall at y 197-229; `New item` is `rgb(0,115,234)` at x 389-466 and its caret carries
 * `border-radius: 0 4px 4px 0` and `aria-label="New item options"`. A control whose feature is
 * active shows a count badge and a tinted background (`rgb(204,229,255)`, measured on the
 * `Filter` control while its pane is open).
 *
 * **Measured correction (round 5o, `discovery/ui-reference/filters/sandbox-badge-*.png`).**
 * An active control is not "label + a separate count chip": monday **renames** it to
 * `<Label> / <n>` — `Sort / 1`, `Filter / 2`, `Hide / 1` — and tints the background
 * `rgb(204,229,255)`. `Group by` shows `Group by / 1` when a column is chosen. `Search` and
 * `Person` never carry a count; `Search` expands into a 266 x 32 input with the placeholder
 * `Search this board` instead of opening a pane.
 */
import type { GroupId } from '@elizmonday/shared';
import { Icon, icons } from '@elizmonday/ui';
import { useState } from 'react';
import { useTable, useTableActions } from './context';
import { Menu, type MenuEntry } from './menus/Menu';
import { SearchPane } from './panes/Search';
import { countRules } from './settings';
import type { PaneKind } from './store';

export function Toolbar() {
  /**
   * B10: the caret was `disabled`. monday's split menu is **220 × 80**, two 32 px rows —
   * `New group of items` · `Import items` `[c:new-item-menu]`.
   */
  const [newMenuAt, setNewMenuAt] = useState<{ x: number; y: number } | null>(null);
  const payload = useTable((state) => state.payload);
  const effective = useTable((state) => state.effective);
  const pane = useTable((state) => state.pane);
  const actions = useTableActions();

  const filterCount = countRules(effective.filter);
  const sortCount = effective.sort.length;
  const hiddenCount = effective.hiddenColumnIds.length;
  const grouped = effective.groupBy !== null;

  const control = (id: string, label: string, icon: keyof typeof icons, target: PaneKind, badge?: number) => {
    const count = badge ?? 0;
    return (
      <button
        type="button"
        className="ez-toolbar__button"
        data-testid={`toolbar-${id}`}
        data-active={pane === target || count > 0 || undefined}
        data-count={count > 0 ? count : undefined}
        aria-pressed={pane === target}
        onClick={() => actions.openPane(target)}
      >
        <Icon icon={icons[icon]} size={16} ignoreFocusStyle />
        {/* Measured: monday renames the control rather than adding a chip — `Sort / 1`. */}
        <span>{count > 0 ? `${label} / ${count}` : label}</span>
      </button>
    );
  };

  const firstGroup = payload?.groups[0]?.id as GroupId | undefined;

  return (
    <div className="ez-toolbar" role="toolbar" aria-label="Board actions" data-testid="board-toolbar">
      <div className="ez-toolbar__new">
        <button
          type="button"
          className="ez-toolbar__primary"
          data-testid="new-item"
          disabled={!firstGroup}
          onClick={() =>
            firstGroup &&
            void actions.addItem(firstGroup, `New ${payload?.board.itemTerminology ?? 'item'}`, 'top')
          }
        >
          {`New ${payload?.board.itemTerminology ?? 'item'}`}
        </button>
        <button
          type="button"
          className="ez-toolbar__primary-caret"
          aria-label="New item options"
          data-testid="new-item-options"
          onClick={(event) => {
            const box = event.currentTarget.getBoundingClientRect();
            setNewMenuAt({ x: box.left, y: box.bottom + 4 });
          }}
        >
          <Icon icon={icons.chevronDown} size={14} ignoreFocusStyle />
        </button>
      </div>

      {/*
        * Measured: `Search` does not open a pane — the control itself becomes the input, and
        * everything to its right slides along (B6 §4.8).
        */}
      {pane === 'search' || effective.search !== '' ? (
        <SearchPane />
      ) : (
        control('search', 'Search', 'search', 'search')
      )}
      {control('person', 'Person', 'person', 'person')}
      {control('filter', 'Filter', 'filter', 'quick-filter', filterCount)}
      {control('sort', 'Sort', 'sort', 'sort', sortCount)}
      {control('hide', 'Hide', 'hide', 'hide', hiddenCount)}
      {control('group-by', 'Group by', 'groupBy', 'group-by', grouped ? 1 : 0)}

      <button
        type="button"
        className="ez-toolbar__icon"
        aria-label="Board options"
        data-testid="toolbar-more"
        onClick={(event) => {
          const box = event.currentTarget.getBoundingClientRect();
          actions.openMenu({ kind: 'board', targetId: 'board', x: box.left, y: box.bottom });
        }}
      >
        <Icon icon={icons.moreActions} size={16} ignoreFocusStyle />
      </button>

      <button type="button" className="ez-toolbar__collapse" aria-label="Collapse toolbar" disabled>
        <Icon icon={icons.chevronDown} size={16} ignoreFocusStyle />
      </button>

      {newMenuAt ? (
        <Menu
          entries={NEW_ITEM_ENTRIES(() => void actions.addGroup())}
          x={newMenuAt.x}
          y={newMenuAt.y}
          width={220}
          label="New item options"
          onClose={() => setNewMenuAt(null)}
        />
      ) : null}
    </div>
  );
}

/** monday's two rows, verbatim `[c:new-item-menu]`. */
const NEW_ITEM_ENTRIES = (addGroup: () => void): MenuEntry[] => [
  { id: 'new-group', label: 'New group of items', onSelect: addGroup, 'data-testid': 'new-group' },
  {
    id: 'import',
    label: 'Import items',
    disabled: true,
    title: 'Importing items from a file arrives with M12; the account migration importer is admin-only.',
  },
];
